import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Store,
  MapPin,
  ExternalLink,
  Eye,
  Edit,
  X,
  Plus,
  Search,
  EyeOff,
} from 'lucide-react'
import { VENDOR_TYPES } from '../utils/mockData'
import { adminVendorsService, adminVendorCategoriesService } from '../services'
import api from '../services/api'
import ProfileAvatar from '../components/ProfileAvatar'
import './Vendors.css'

const statusToDisplay = (s) => (s === 'active' || s === 'approved' ? 'Shown' : 'Hidden')
const displayToStatus = (s) => (s === 'Shown' ? 'active' : 'suspended')
const toReviewStatusLabel = (rawStatus) => {
  const s = String(rawStatus || '').trim().toLowerCase()
  if (s === 'pending') return 'Under Review'
  if (s === 'rejected') return 'Rejected'
  if (s === 'approved' || s === 'active' || s === 'shown') return 'Approved'
  if (s === 'suspended') return 'Suspended'
  if (s === 'hidden') return 'Hidden'
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Under Review'
}

const normalizeVendorForList = (v) => {
  const rawAccountStatus =
    v.accountStatus ??
    v.account_status ??
    v.status
  const visibilityStatus =
    (v.status === 'Shown' || v.status === 'Hidden')
      ? v.status
      : statusToDisplay(rawAccountStatus)
  return {
    ...v,
    status: visibilityStatus,
    reviewStatus: toReviewStatusLabel(rawAccountStatus),
  }
}

const getVendorCategoriesLabel = (vendor) => {
  const raw =
    vendor.activityCategories ??
    vendor.activity_categories ??
    vendor.categories ??
    vendor.category

  if (Array.isArray(raw) && raw.length > 0) {
    const first = raw[0]
    if (typeof first === 'string' && first.trim()) return first
    if (first && typeof first === 'object') {
      const name = first.name ?? first.label ?? first.title
      if (typeof name === 'string' && name.trim()) return name
    }
  }

  if (raw && typeof raw === 'object') {
    const name = raw.name ?? raw.label ?? raw.title
    if (typeof name === 'string' && name.trim()) return name
  }

  if (typeof raw === 'string' && raw.trim()) return raw

  return '—'
}

const Vendors = () => {
  const navigate = useNavigate()
  const [vendors, setVendors] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('All')
  const [filterLocation, setFilterLocation] = useState('All')
  const [filterStatus, setFilterStatus] = useState('All')
  const [showModal, setShowModal] = useState(false)
  const [selectedVendor, setSelectedVendor] = useState(null)
  const [partnerCategories, setPartnerCategories] = useState([])
  const [allowedLocations, setAllowedLocations] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  useEffect(() => {
    Promise.all([
      adminVendorsService.list(),
      adminVendorCategoriesService.list(),
      api.get('/admin/vendors/locations').catch(() => ({ items: [] }))
    ]).then(([list, categories, locationsRes]) => {
      setVendors((list || []).map(normalizeVendorForList))
      setPartnerCategories(categories || [])
      setAllowedLocations(locationsRes?.items || [])
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  const filteredVendors = vendors.filter((vendor) => {
    const matchesSearch =
      (vendor.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (vendor.location || '').toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = filterType === 'All' || (vendor.type || vendor.vendorType) === filterType
    const matchesLocation = filterLocation === 'All' || vendor.location === filterLocation
    const matchesStatus = filterStatus === 'All' || vendor.reviewStatus === filterStatus
    return matchesSearch && matchesType && matchesLocation && matchesStatus
  })

  // Pagination
  const totalPages = Math.ceil(filteredVendors.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedVendors = filteredVendors.slice(startIndex, startIndex + itemsPerPage)


  return (
    <div className="vendors-page">
      <div className="page-header">
        <div className="header-titles">
          <h1>Partners Management</h1>
          <p>Manage and monitor all partners on the platform.</p>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="page-actions">
        <div style={{ display: 'flex', gap: '1rem', flex: 1, alignItems: 'center' }}>
          <input
            type="text"
            className="search-input"
            placeholder="Search partners by name or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            className="filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="All">All Types</option>
            {VENDOR_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <select
            className="filter-select"
            value={filterLocation}
            onChange={(e) => setFilterLocation(e.target.value)}
          >
            <option value="All">All Locations</option>
            {allowedLocations.map(loc => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Under Review">Under Review</option>
            <option value="Rejected">Rejected</option>
            <option value="Approved">Approved</option>
            <option value="Suspended">Suspended</option>
            <option value="Hidden">Hidden</option>
          </select>
        </div>
      </div>

      {/* Vendors Table */}
      <div className="vendors-table-container">
        {loading ? (
          <p style={{ padding: '2rem' }}>Loading partners...</p>
        ) : (
          <table className="vendors-table">
            <thead>
              <tr>
                <th>Partner</th>
                <th>Categories</th>
                <th>Appearances</th>
                <th>Revenue</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedVendors.map((vendor) => (
                <tr key={vendor.id}>
                  <td>
                    <div className="vendor-info">
                      <div className="vendor-avatar">
                        <ProfileAvatar
                          src={vendor.profilePhotoUrl}
                          alt={vendor.name || ''}
                          className="vendor-logo"
                        />
                      </div>
                      <div className="vendor-details">
                        <div className="vendor-name">{vendor.name || vendor.businessName}</div>
                        <div className="vendor-location">
                          <MapPin size={12} />
                          {vendor.location || '—'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="vendor-type-badge">
                      <Store size={14} />
                      {getVendorCategoriesLabel(vendor)}
                    </span>
                  </td>
                  <td>
                    <div>
                      <div className="metric-value">{vendor.appearanceCount}</div>
                      <div className="metric-label">in plans</div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div className="metric-value">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'EGP',
                          minimumFractionDigits: 0,
                        }).format(vendor.revenue)}
                      </div>
                      <div className="metric-label">total</div>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${vendor.reviewStatus.toLowerCase().replace(/\s+/g, '-')}`}>
                      {vendor.reviewStatus}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="action-btn"
                        onClick={() => navigate(`/vendors/${vendor.id}`)}
                        title="View Details"
                      >
                        <ExternalLink size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        <div className="pagination">
          <div className="pagination-info">
            Showing {startIndex + 1} to{' '}
            {Math.min(startIndex + itemsPerPage, filteredVendors.length)} of{' '}
            {filteredVendors.length} partners
          </div>
          <div className="pagination-buttons">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Vendor Modal */}
      {showModal && (
        <VendorModal
          vendor={selectedVendor}
          partnerCategories={partnerCategories}
          allowedLocations={allowedLocations}
          onClose={() => setShowModal(false)}
          onSave={async (vendorData) => {
            try {
              const payload = {
                ...vendorData,
                partnerCategory: vendorData.partnerCategory || '',
              }

              if (selectedVendor) {
                const updated = await adminVendorsService.update(selectedVendor.id, payload)
                setVendors((prev) =>
                  prev.map((v) =>
                    v.id === selectedVendor.id
                      ? normalizeVendorForList(updated)
                      : v
                  )
                )
              } else {
                const created = await adminVendorsService.create(payload)
                setVendors((prev) => [
                  normalizeVendorForList(created),
                  ...prev,
                ])
              }
              setShowModal(false)
            } catch (e) {
              console.error(e)
            }
          }}
        />
      )}
    </div>
  )
}

// Vendor Modal Component
const VendorModal = ({ vendor, partnerCategories, allowedLocations, onClose, onSave }) => {
  const getInitialPartnerCategory = (value) => {
    const raw =
      value?.activityCategories ??
      value?.activity_categories ??
      value?.categories ??
      value?.category

    if (Array.isArray(raw) && raw.length > 0) return raw[0]
    if (typeof raw === 'string' && raw.trim()) return raw.trim()
    return ''
  }

  const [formData, setFormData] = useState(
    vendor || {
      name: '',
      type: 'Restaurant',
      profilePhotoUrl: '',
      location: '',
      contact: '',
      email: '',
      description: '',
      capacity: '',
      status: 'Shown',
      partnerCategory: getInitialPartnerCategory(vendor),
    }
  )

  useEffect(() => {
    if (!vendor) return
    setFormData((prev) => ({
      ...prev,
      partnerCategory: getInitialPartnerCategory(vendor),
    }))
  }, [vendor])

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave(formData)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Edit Partner</h2>
          <button type="button" className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Partner Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Type *</label>
                <select
                  className="form-select"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                >
                  {VENDOR_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Logo URL</label>
              <input
                type="url"
                className="form-input"
                value={formData.profilePhotoUrl}
                onChange={(e) => setFormData({ ...formData, profilePhotoUrl: e.target.value })}
                placeholder="https://example.com/logo.png"
              />
              {formData.profilePhotoUrl && (
                <div
                  style={{
                    marginTop: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <img
                    src={formData.profilePhotoUrl}
                    alt="Logo preview"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '0.5rem',
                      objectFit: 'cover',
                    }}
                    onError={(e) => (e.target.style.display = 'none')}
                  />
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    Logo preview
                  </span>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Location *</label>
              <select
                className="form-select"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
              >
                <option value="">Select location</option>
                {allowedLocations.map(loc => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Contact *</label>
                <input
                  type="tel"
                  className="form-input"
                  value={formData.contact}
                  onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-textarea"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the partner..."
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">Partner Capacity (Avg. People)</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                className="form-input"
                value={formData.capacity}
                onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                placeholder="e.g. 50"
              />
            </div>

            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">Partner Category *</label>
              <select
                className="form-select"
                value={formData.partnerCategory || ''}
                onChange={(e) => setFormData({ ...formData, partnerCategory: e.target.value })}
                required
              >
                <option value="">Select partner category</option>
                {partnerCategories.map((cat) => (
                  <option key={cat.id} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="Shown">Shown</option>
                <option value="Hidden">Hidden</option>
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Vendors
